import { useTranslation } from '@gympass/i18n';

import config from 'config/index';
import { useEventTracking } from 'providers/EventTrackingProvider';
import { useLead } from 'providers/LeadProvider';
import type { Country, Language } from 'services/i18n';
import {
  LOCALE_COOKIE,
  cookies,
  getAvailableCountries,
  LANGUAGES_BY_COUNTRY,
} from 'services/i18n';
import {
  LanguageSelector,
  type LanguageSelectorOptions,
} from '@gympass/partners-react-forms';

function getLanguageOptions(): LanguageSelectorOptions<Country, Language> {
  return Object.fromEntries(
    getAvailableCountries().map(country => [
      country,
      LANGUAGES_BY_COUNTRY[country],
    ]),
  );
}

export function LanguageSelection() {
  const { i18n, t } = useTranslation();

  const { recordClick } = useEventTracking();
  const { updateLead } = useLead();

  const handleChangeLanguage = (language: Language) => {
    cookies.set(LOCALE_COOKIE, language, {
      domain: config.PARTNERS_DOMAIN,
      path: '/',
    });

    i18n.changeLanguage(language, () => {
      recordClick({
        category: 'language',
        label: 'update_language',
        language,
      });

      updateLead({ lead: { language } });
    });
  };

  return (
    <LanguageSelector<Country, Language>
      onChangeLanguage={handleChangeLanguage}
      locale={i18n.language as Language}
      options={getLanguageOptions()}
      labels={{
        title: t('common.language.selector.title'),
        ariaLabel: t('common.language.selector.aria_label'),
        searchPlaceholder: t('common.language.selector.search.placeholder'),
        noResultsTitle: t('common.language.selector.search.empty.title'),
        noResultsSubtitle: t('common.language.selector.search.empty.subtitle'),
        subtitle: t('common.language.selector.subtitle'),
      }}
    />
  );
}
